import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatedPaginatorIntl } from 'app/core/transloco/paginator-intl';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { LANG_STORAGE_KEY } from './core/transloco/transloco.constants';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth as provideFireAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { environment } from 'environments/environment';
import { MockApiService } from './mock-api';

export const ProjectFields = 'id,uuid,app,title,description,trace_id,website,status,visibility,timezone,logo_photo,cover_photo,owners,contacts,meta,address,app_settings';
export const CompanyFields = 'id,uuid,title,description,contacts,address,logo_photo,type,parent,meta,app_settings,trace_id,created,last_modified';
export const SiteFields = 'id,title,description,project,company,meta,app_settings,trace_id,created,last_modified';

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
        provideAnimations(),
        provideHttpClient(),
        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({ paramsInheritanceStrategy: 'always' })
            // withComponentInputBinding()
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'ms',
                        label: 'Malay',
                    },
                    {
                        id: 'zh',
                        label: '简体中文',
                    }
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                // The language picked in the header (see LanguagesComponent), when it's still offered
                const available = (translocoService.getAvailableLangs() as { id: string }[]).map(lang => lang.id);
                let savedLang: string | null = null;
                try { savedLang = localStorage.getItem(LANG_STORAGE_KEY); } catch { }
                const startLang = savedLang && available.includes(savedLang) ? savedLang : translocoService.getDefaultLang();
                translocoService.setActiveLang(startLang);

                return () => firstValueFrom(translocoService.load(startLang));
            })();
            return initializerFn();
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideFireAuth(() => getAuth()),
        provideFirestore(() => getFirestore())
        // importProvidersFrom([

        // ])
    ],
};
